import React, { ReactElement, useState } from 'react'
import { StyleSheet, TextStyle, TouchableOpacity, View, ViewProps, ViewStyle } from 'react-native'

import { Color, Radius, Spacing } from '../../style'
import { Icon } from '../icon'
import { Name } from '../icon/icon'
import { Text } from '../typography'

interface StyleConfig {
  container?: ViewStyle
  message?: TextStyle
  icon?: TextStyle
}

const defaultConfig: StyleConfig = {
  container: {
    backgroundColor: Color.premiumBlack,
  },
}
const infoConfig: StyleConfig = {
  container: {
    backgroundColor: Color.greyBlue,
  },
}
const errorConfig: StyleConfig = {
  container: {
    backgroundColor: Color.soothingPink,
  },
}
const warningConfig: StyleConfig = {
  container: {
    backgroundColor: Color.collCopper,
  },
}
const successConfig: StyleConfig = {
  container: {
    backgroundColor: Color.refinedGreen,
  },
}
const helpConfig: StyleConfig = {
  container: {
    backgroundColor: Color.modifiedGrey,
  },
}
const lightConfig: StyleConfig = {
  container: {
    backgroundColor: Color.simplyWhite,
    shadowOffset: {
      width: 1,
      height: 4,
    },
    shadowOpacity: 0.1,
    shadowRadius: 30,
    shadowColor: Color.premiumBlack,
  },
  message: {
    color: Color.premiumBlack,
  },
  icon: {
    color: Color.premiumBlack,
  },
}

const StatusConfig = {
  default: defaultConfig,
  info: infoConfig,
  error: errorConfig,
  warning: warningConfig,
  success: successConfig,
  help: helpConfig,
  light: lightConfig,
}

const styles = StyleSheet.create({
  container: {
    paddingVertical: Spacing.S,
    paddingHorizontal: Spacing.M,
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: Radius.S,
  },
  message: {
    flex: 1,
    color: Color.simplyWhite,
  },
  close: {
    width: 18,
    height: 18,
  },
  icon: {
    color: Color.simplyWhite,
    width: 18,
    height: 18,
  },
})

export type Status = keyof typeof StatusConfig
export const Statuses = Object.keys(StatusConfig) as Status[]

export interface Props extends Omit<ViewProps, 'id'> {
  status?: Status
  message?: string | ReactElement
  closeable?: boolean
  onClose?: () => void
  left?: Name | ReactElement
  right?: Name | ReactElement
}
const Banner = (props: Props) => {
  const [closed, setClosed] = useState(false)
  const { status, message, closeable, onClose, left, right, style, ...otherProps } = props
  const statusConfig = StatusConfig[status || 'default']
  const containerStyle = [styles.container, statusConfig.container, style]
  const close = () => {
    setClosed(true)
    onClose && onClose()
  }
  if (closed) return null
  const leftStyle = [(!!message || !!right) && { marginRight: Spacing.S }, statusConfig.icon]
  const rightStyle = [(!!message || !!left) && { marginLeft: Spacing.S }, statusConfig.icon]
  const closeStyle = [!right && { marginLeft: Spacing.S }, statusConfig.icon]
  const messageStyle = [styles.message, statusConfig.message]
  return (
    <View style={containerStyle} {...otherProps}>
      {left &&
        (typeof left === 'string' ? (
          <Icon name={left} size={16} style={[styles.icon, leftStyle]} />
        ) : (
          <View style={leftStyle}>{left}</View>
        ))}
      {!!message && (typeof message === 'string' ? <Text style={messageStyle}>{message}</Text> : message)}
      {right &&
        (typeof right === 'string' ? (
          <Icon name={right} size={16} style={[styles.icon, rightStyle]} />
        ) : (
          <View style={leftStyle}>{right}</View>
        ))}
      {closeable && (
        <TouchableOpacity onPress={close} testID="Banner.Close">
          <Icon name="close" color={Color.titaniumWhite} style={[styles.close, closeStyle]} />
        </TouchableOpacity>
      )}
    </View>
  )
}

export default Banner
